/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo } from 'react';

import { useLocation } from 'js/lib/useRouter';

import { Hidden, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';

import useDebounce from 'bundles/common/hooks/useDebounce';
import useIsOnLocaleSubfolderRoute from 'bundles/internationalization-lib/hooks/useIsOnLocaleSubfolderRoute';
import useUserAgent from 'bundles/page/hooks/useUserAgent';
import { styles as SearchPageBodyStyles } from 'bundles/search-common/components/SearchPageBody';
import SearchResultsHeader from 'bundles/search-common/components/SearchResultsHeader';
import SortByIndex from 'bundles/search-common/components/SortByIndex';
import InlineSearchFilters from 'bundles/search-common/components/filters/InlineSearchFilters';
import SearchFilters from 'bundles/search-common/components/filters/SearchFilters';
import useFilterListOrder from 'bundles/search-common/components/filters/useFilterListOrder';
import { SEARCH_FILTERS_INDEX_ID, SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { DEFAULT_SEARCH_FILTERS_SORTED_BY_USAGE, FILTER_TYPE } from 'bundles/search-common/constants/filter';
import type { FilterType } from 'bundles/search-common/constants/filter';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { SearchPageEventingData } from 'bundles/search-common/types';
import { inferQueryFromParams } from 'bundles/search-common/utils/eventingUtils';
import { getShouldUseRemoveFilterHack } from 'bundles/search-common/utils/experimentUtils';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';
import { createFilterKeyToOptionsMap, scrollUpIfNecessary } from 'bundles/search-common/utils/utils';

import _t from 'i18n!nls/search-common';

/**
 * Container component for the entire search filters section.
 * Handles layout, orchestration, and cross-group logic for all filters on the search page.
 * - Renders filter headers, sticky sidebar, and manages filter state across groups.
 * - Uses SearchFilters for rendering individual filter groups.
 */

type Props = {
  optimisticActiveFilterCount: number;
  isSEOEntityPage: boolean;
  eventingData: SearchPageEventingData;
  filterType: FilterType;
  showMobileSortBy?: boolean;
  shouldShowSectionTitleForInlineFilters?: boolean;
};

const styles = {
  searchPageFilters: css`
    height: 100%;
  `,
  inlineStickyContainer: css`
    height: 100%;
    width: 100%;
    min-width: 50px;
    align-self: flex-start;
    padding-right: 16px; /* give space for auto-hide scrollbar */
  `,
  inlineStickyFilterWrapper: css`
    position: sticky;
    top: 125px;
    padding-bottom: 32px;

    &::after {
      /* gradient  */
      content: '';
      position: sticky;
      bottom: 0;
      height: 100px;
      width: 100%;
      background: linear-gradient(to top, var(--cds-color-neutral-background-primary) 0%, rgb(255 255 255 / 0%) 100%);
      pointer-events: none;
      z-index: 1;
      display: block;
    }
  `,
  /* because the header changes height when scrolling up */
  inlineStickyFilterWrapperWhenPageIsScrolled: css`
    top: 85px;
  `,
  scrollableFilters: css`
    height: calc(100vh - 180px); /* viewport height minus full heading height plus padding */
    overflow-y: auto;
    visibility: hidden; /* auto-hide scrollbar for filters */
    padding-right: 8px;
    padding-left: 3px;
    transition: visibility 0.7s; /* scrollbar hide delay */

    /* the magic that shows the scrollbar on hover */
    &:hover,
    &:focus,
    .filterContent {
      visibility: visible;
    }
  `,
  scrollableFiltersWhenPageIsScrolled: css`
    height: calc(100vh - 145px); /* viewport height minus shrunk heading height plus padding */
  `,
};

function scrollUpOnFilterChange(disableScrollAnimation: boolean) {
  scrollUpIfNecessary({
    disableScrollAnimation,
    mustBeScrolledPastThisElementId: 'search-results-header-wrapper',
  });
}

const SearchPageFilters = (props: Props) => {
  const {
    optimisticActiveFilterCount,
    isSEOEntityPage,
    eventingData,
    filterType,
    showMobileSortBy = false,
    shouldShowSectionTitleForInlineFilters = false,
  } = props;
  const [headerIsScrolledUp, setHeaderIsScrolledUp] = React.useState(false);

  const debouncedScrollHandler = useDebounce(() => {
    // warning: highly coupled to implementation of the page header. If the header changes,
    // this should degrade gracefully, i.e. the height of the filters will simply stay the same.
    const headerIsTuckedUp = !!document.getElementsByClassName('hide-smart-scroll-container')[0];
    setHeaderIsScrolledUp(headerIsTuckedUp);
  }, 0);
  useEffect(() => {
    // listening to header height changes to support sticky filters
    window.addEventListener('scroll', debouncedScrollHandler, { passive: true });
    return () => {
      window.removeEventListener('scroll', debouncedScrollHandler);
    };
  }, [debouncedScrollHandler]);

  const userAgent = useUserAgent();
  const isMobile = useMediaQuery(breakpoints.down('sm'), {
    defaultMatches: userAgent?.isMobileBrowser || userAgent?.isMobile,
  });
  const { query: queryParams } = useLocation();
  const { query: searchQueryFromParams } = queryParams;
  const isOnLocaleSubfolderRoute = useIsOnLocaleSubfolderRoute();
  const { getSearchResults, addFilters, removeFilters, clearFilters, setFiltersByFacet, setSortBy, isLoading } =
    useSearch();
  const isConsumerSearch = useIsConsumerSerp();
  const isEQP = useIsEqp();
  const removeFilterHack = (isConsumerSearch || isEQP) && getShouldUseRemoveFilterHack();
  const searchData = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const filterData = removeFilterHack
    ? getSearchResults(SEARCH_MAIN_INDEX_ID)
    : getSearchResults(SEARCH_FILTERS_INDEX_ID);
  const { facets, pagination, facetFilters, hitsDisplayedCount = 0, sortBy } = searchData || {};
  const allFilters = filterData?.facets;
  const totalHits = pagination?.totalElements || undefined;
  const activeFilters = useMemo(() => [...new Set(facetFilters?.flat())], [facetFilters]);
  const filterListOrder = useFilterListOrder(DEFAULT_SEARCH_FILTERS_SORTED_BY_USAGE);
  const filterKeyToOptionsMap = useMemo(
    () => createFilterKeyToOptionsMap(facets, facetFilters, filterListOrder, allFilters),
    [allFilters, facetFilters, facets, filterListOrder]
  );

  const isOnEQPSubfolderRoute = isOnLocaleSubfolderRoute && isSEOEntityPage;

  // Derive behavior from filterType
  const isExpandableFilters = filterType === FILTER_TYPE.EXPANDABLE;
  const isInlineFilters = filterType === FILTER_TYPE.INLINE;

  return (
    <div role="region" aria-label={_t('Filters')} css={styles.searchPageFilters}>
      <SearchResultsHeader
        searchQuery={inferQueryFromParams(searchQueryFromParams)}
        numberOfHits={totalHits || undefined}
        shouldHide={!isMobile}
        isLoading={isLoading && hitsDisplayedCount === 0}
        activeFilterCount={optimisticActiveFilterCount}
        lessenHeaderFocus={true}
        hideHits={true}
      />
      <div
        css={[
          SearchPageBodyStyles.header,
          isMobile && SearchPageBodyStyles.headerMobile,
          SearchPageBodyStyles.fullHeight,
        ]}
      >
        <div css={[SearchPageBodyStyles.mobileSortByLayoutShim, SearchPageBodyStyles.fullHeight]}>
          {/* where the mobile problem starts */}
          <Hidden smDown css={[isInlineFilters && styles.inlineStickyContainer]}>
            <div
              css={[
                isInlineFilters && styles.inlineStickyFilterWrapper,
                isInlineFilters && headerIsScrolledUp && styles.inlineStickyFilterWrapperWhenPageIsScrolled,
              ]}
            >
              {isInlineFilters && (
                <Typography2 variant="subtitleLarge" component="h2" css={{ marginTop: '8px', marginBottom: '32px' }}>
                  {_t('Filter by')}
                </Typography2>
              )}
              <div
                css={[
                  isInlineFilters && styles.scrollableFilters,
                  isInlineFilters && headerIsScrolledUp && styles.scrollableFiltersWhenPageIsScrolled,
                ]}
              >
                <div className="filterContent">
                  {isInlineFilters && (
                    <InlineSearchFilters
                      eventingData={eventingData}
                      shouldShowSectionTitle={shouldShowSectionTitleForInlineFilters}
                      onFilterValueChange={() => scrollUpOnFilterChange(true)}
                      isMobile={isMobile}
                    />
                  )}
                </div>
              </div>
            </div>
          </Hidden>
          {(isExpandableFilters || isMobile) && (
            <SearchFilters
              filters={filterKeyToOptionsMap}
              activeFilters={activeFilters}
              eventingData={eventingData}
              isMobile={isMobile}
              addFilters={(filter: string) => addFilters(SEARCH_MAIN_INDEX_ID, [filter])}
              setFiltersByFacet={(facetName: string, newFilters: string[]) => {
                scrollUpOnFilterChange(false);
                setFiltersByFacet(SEARCH_MAIN_INDEX_ID, facetName, newFilters);
              }}
              removeFilters={(filter: string) => removeFilters(SEARCH_MAIN_INDEX_ID, [filter])}
              clearFilters={() => clearFilters(SEARCH_MAIN_INDEX_ID)}
              filtersListOrder={filterListOrder}
              shouldHideSkillsFilter={isOnEQPSubfolderRoute}
            />
          )}
        </div>
        {/* mobile sortby */}
        {isMobile && showMobileSortBy && (
          <div>
            <SortByIndex
              setSortBy={(sortByValue) => setSortBy(SEARCH_MAIN_INDEX_ID, sortByValue)}
              sortByValue={sortBy}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPageFilters;
